// WbVehicleDetailPage — /weighbridge/vehicles/:enc_id (and /new).
//
// Phase C3 Group 2. Create + edit form for a single vehicle. Delete and
// tare management (set / clear) live on this page too.
//
// The main form NEVER touches tare — server tracks `stored_tare_set_at` /
// `stored_tare_set_by` and the audit log. POST /:enc_id/tare and DELETE
// /:enc_id/tare are the only legitimate paths, both invoked via the tare
// section below. ANPR history is a placeholder until agent telemetry lands.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import QRCode from 'react-qr-code';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Modal } from '../../../components/ui/Modal/Modal';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useClearVehicleTare,
  useCreateVehicle,
  useDeleteVehicle,
  useSetVehicleTare,
  useUpdateVehicle,
  useWbVehicle,
  type CreateVehicleInput,
  type VehicleType,
} from '../hooks/useWbVehicles';
import {
  WbHaulierSearch,
  type WbHaulierSearchValue,
} from '../components/WbHaulierSearch';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbVehicleDetailPage.module.css';

const VEHICLE_TYPES: ReadonlyArray<{ value: VehicleType; label: string }> = [
  { value: 'rigid', label: 'Rigid' },
  { value: 'artic', label: 'Artic' },
  { value: 'van', label: 'Van' },
  { value: 'skip', label: 'Skip' },
  { value: 'roro', label: 'RoRo' },
  { value: 'other', label: 'Other' },
];

interface FormState {
  registration: string;
  vehicle_type: VehicleType;
  haulier: WbHaulierSearchValue | null;
  max_capacity_kg: string; // text input — empty === null
  notes: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  registration: '',
  vehicle_type: 'other',
  haulier: null,
  max_capacity_kg: '',
  notes: '',
  is_active: true,
};

function relativeFromNow(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'in the future';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

export function WbVehicleDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || enc_id === undefined;

  const vehicleQuery = useWbVehicle(isNew ? undefined : enc_id);
  const createMut = useCreateVehicle();
  const updateMut = useUpdateVehicle(isNew ? '' : (enc_id as string));
  const deleteMut = useDeleteVehicle(isNew ? '' : (enc_id as string));
  const setTareMut = useSetVehicleTare(isNew ? '' : (enc_id as string));
  const clearTareMut = useClearVehicleTare(isNew ? '' : (enc_id as string));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [tareModalOpen, setTareModalOpen] = useState(false);
  const [confirmClearTareOpen, setConfirmClearTareOpen] = useState(false);
  const [tareForm, setTareForm] = useState({ weight_kg: '', reason: '' });
  const [tareError, setTareError] = useState<string | null>(null);

  // Hydrate the form once the GET resolves.
  useEffect(() => {
    if (isNew) return;
    const v = vehicleQuery.data?.vehicle;
    if (!v) return;
    setForm({
      registration: v.registration,
      vehicle_type: v.vehicle_type,
      haulier: v.haulier ? { enc_id: v.haulier.enc_id, name: v.haulier.name } : null,
      max_capacity_kg:
        v.max_capacity_kg !== null ? String(v.max_capacity_kg) : '',
      notes: v.notes ?? '',
      is_active: v.is_active,
    });
  }, [isNew, vehicleQuery.data]);

  const vehicle = vehicleQuery.data?.vehicle;

  function validate(): boolean {
    const errs: Record<string, string> = {};
    const reg = form.registration.trim().replace(/\s+/g, '').toUpperCase();
    if (!reg) errs['registration'] = 'Registration is required.';
    if (reg.length > 20) errs['registration'] = 'Registration must be 20 characters or fewer.';
    if (form.max_capacity_kg !== '') {
      const n = Number(form.max_capacity_kg);
      if (!Number.isFinite(n) || n <= 0) {
        errs['max_capacity_kg'] = 'Max capacity must be a positive number.';
      }
    }
    if (form.notes.length > 2000) {
      errs['notes'] = 'Notes must be 2000 characters or fewer.';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function buildBody(): CreateVehicleInput {
    const reg = form.registration.trim().replace(/\s+/g, '').toUpperCase();
    const body: CreateVehicleInput = {
      registration: reg,
      vehicle_type: form.vehicle_type,
      haulier_id: form.haulier?.enc_id ?? null,
      max_capacity_kg:
        form.max_capacity_kg === '' ? null : Number(form.max_capacity_kg),
      notes: form.notes.trim() === '' ? null : form.notes.trim(),
      is_active: form.is_active,
    };
    return body;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    const body = buildBody();
    if (isNew) {
      createMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/vehicles'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    } else {
      updateMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/vehicles'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    }
  }

  function handleDeleteConfirm() {
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        navigate('/weighbridge/vehicles');
      },
      onError: (err) => {
        setConfirmDeleteOpen(false);
        setError(extractErrorMessage(err));
      },
    });
  }

  function openTareModal() {
    setTareError(null);
    setTareForm({ weight_kg: '', reason: '' });
    setTareModalOpen(true);
  }

  function handleTareSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTareError(null);
    const weight = Number(tareForm.weight_kg);
    if (!Number.isFinite(weight) || weight <= 0) {
      setTareError('Weight must be a positive number.');
      return;
    }
    const body: { weight_kg: number; reason?: string } = { weight_kg: weight };
    if (tareForm.reason.trim() !== '') body.reason = tareForm.reason.trim();
    setTareMut.mutate(body, {
      onSuccess: () => setTareModalOpen(false),
      onError: (err) => setTareError(extractErrorMessage(err)),
    });
  }

  function handleClearTareConfirm() {
    clearTareMut.mutate(undefined, {
      onSuccess: () => setConfirmClearTareOpen(false),
      onError: (err) => {
        setConfirmClearTareOpen(false);
        setError(extractErrorMessage(err));
      },
    });
  }

  const isSaving = createMut.isPending || updateMut.isPending;
  const isLoadingExisting = !isNew && vehicleQuery.isLoading;

  const handlePrintSticker = useCallback(() => {
    if (!vehicle) return;
    const qrEl = document.querySelector('[data-qr-sticker]');
    const printWindow = window.open('', '_blank');
    if (!printWindow || !qrEl) return;
    printWindow.document.write(`
      <html><head><title>Vehicle Sticker — ${vehicle.registration}</title>
      <style>
        body { margin: 0; display: flex; flex-direction: column;
               align-items: center; justify-content: center;
               font-family: monospace; padding: 16px; }
        svg { width: 200px; height: 200px; }
        p { font-size: 18px; font-weight: bold; letter-spacing: 0.15em; margin-top: 8px; }
        @media print { body { margin: 0; } }
      </style></head>
      <body>
        ${qrEl.innerHTML}
        <p>${vehicle.registration}</p>
        <script>window.onload = () => window.print();<\/script>
      </body></html>
    `);
    printWindow.document.close();
  }, [vehicle]);

  const tareSummary = useMemo(() => {
    if (!vehicle) return null;
    if (vehicle.stored_tare_kg === null || vehicle.stored_tare_set_at === null) {
      return { label: 'Stored tare not set', meta: null as string | null };
    }
    const formatted = `${vehicle.stored_tare_kg.toLocaleString()} kg`;
    const meta = `set ${relativeFromNow(vehicle.stored_tare_set_at)}`;
    return { label: formatted, meta };
  }, [vehicle]);

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Vehicle' : form.registration || 'Edit Vehicle'}
        subtitle={isNew ? 'Add a new vehicle' : 'Edit vehicle details'}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/vehicles')}
          >
            Back to Vehicles
          </Button>
        }
      />

      {isLoadingExisting ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading vehicle" />
          </div>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <Card>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Registration</span>
                <input
                  type="text"
                  required
                  maxLength={20}
                  value={form.registration}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, registration: e.target.value }))
                  }
                  onBlur={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      registration: e.target.value
                        .replace(/\s+/g, '')
                        .toUpperCase(),
                    }))
                  }
                />
                {fieldErrors['registration'] ? (
                  <span className={styles.fieldError}>
                    {fieldErrors['registration']}
                  </span>
                ) : null}
              </label>

              <label className={styles.field}>
                <span>Vehicle type</span>
                <select
                  value={form.vehicle_type}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      vehicle_type: e.target.value as VehicleType,
                    }))
                  }
                >
                  {VEHICLE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span>Haulier</span>
                <WbHaulierSearch
                  value={form.haulier}
                  onChange={(next) =>
                    setForm((prev) => ({ ...prev, haulier: next }))
                  }
                />
              </label>

              <label className={styles.field}>
                <span>Max capacity (kg)</span>
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={form.max_capacity_kg}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      max_capacity_kg: e.target.value,
                    }))
                  }
                  placeholder="Optional"
                />
                <span className={styles.fieldHelp}>
                  Used for overweight detection. Leave blank to disable.
                </span>
                {fieldErrors['max_capacity_kg'] ? (
                  <span className={styles.fieldError}>
                    {fieldErrors['max_capacity_kg']}
                  </span>
                ) : null}
              </label>

              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                  }
                />
                <span>Active</span>
              </label>
            </div>

            <label className={styles.field} style={{ marginTop: '1rem' }}>
              <span>Notes</span>
              <textarea
                rows={3}
                maxLength={2000}
                value={form.notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Optional"
              />
              {fieldErrors['notes'] ? (
                <span className={styles.fieldError}>{fieldErrors['notes']}</span>
              ) : null}
            </label>
          </Card>

          {!isNew && vehicle ? (
            <Card>
              <h3 className={styles.sectionHeading}>Stored tare</h3>
              <div className={styles.tareSummary}>
                <span className={styles.tareValue}>
                  {tareSummary?.label}
                </span>
                {tareSummary?.meta ? (
                  <span className={styles.tareMeta}>{tareSummary.meta}</span>
                ) : null}
              </div>
              <div className={styles.tareActions}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={openTareModal}
                >
                  {vehicle.stored_tare_kg !== null
                    ? 'Update Tare'
                    : 'Set Tare'}
                </Button>
                {vehicle.stored_tare_kg !== null ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setConfirmClearTareOpen(true)}
                  >
                    Clear Stored Tare
                  </Button>
                ) : null}
              </div>
            </Card>
          ) : null}

          {!isNew && vehicle?.is_active ? (
            <Card>
              <h3 className={styles.sectionHeading}>Vehicle QR Sticker</h3>
              <p className={styles.placeholder} style={{ marginBottom: '1rem' }}>
                Scan with the USB barcode scanner to auto-fill this vehicle on
                the weighbridge ticket.
              </p>
              <div className={styles.qrWrapper} data-qr-sticker>
                <QRCode value={vehicle.uuid} size={160} />
                <p className={styles.qrReg}>{vehicle.registration}</p>
              </div>
              <div className={styles.qrActions}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handlePrintSticker}
                >
                  Print Sticker
                </Button>
              </div>
            </Card>
          ) : null}

          {!isNew ? (
            <Card>
              <h3 className={styles.sectionHeading}>ANPR history</h3>
              <p className={styles.placeholder}>
                ANPR read history will surface here once the agent telemetry
                phase lands.
              </p>
            </Card>
          ) : null}

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.actions}>
            <div>
              {!isNew ? (
                <Button
                  type="button"
                  variant="danger"
                  icon={Trash2}
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={isSaving}
                >
                  Delete
                </Button>
              ) : null}
            </div>
            <div className={styles.rightActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/weighbridge/vehicles')}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isSaving}>
                {isNew ? 'Create Vehicle' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      )}

      <ConfirmModal
        open={confirmDeleteOpen}
        title="Delete vehicle?"
        message={
          <p>
            Delete <strong>{form.registration || 'this vehicle'}</strong>? This
            cannot be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteOpen(false)}
        loading={deleteMut.isPending}
      />

      <ConfirmModal
        open={confirmClearTareOpen}
        title="Clear stored tare?"
        message={
          <p>
            Remove the stored tare for{' '}
            <strong>{form.registration || 'this vehicle'}</strong>? The
            weighbridge will require a fresh empty weigh next time it crosses.
          </p>
        }
        variant="danger"
        confirmLabel="Clear tare"
        onConfirm={handleClearTareConfirm}
        onCancel={() => setConfirmClearTareOpen(false)}
        loading={clearTareMut.isPending}
      />

      <Modal
        open={tareModalOpen}
        onClose={() => setTareModalOpen(false)}
        title={vehicle?.stored_tare_kg !== null ? 'Update stored tare' : 'Set stored tare'}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setTareModalOpen(false)}
              disabled={setTareMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTareSubmit}
              loading={setTareMut.isPending}
            >
              Save tare
            </Button>
          </>
        }
      >
        <form onSubmit={handleTareSubmit} className={styles.modalForm}>
          <label className={styles.field}>
            <span>Weight (kg)</span>
            <input
              type="number"
              required
              min={0}
              step="any"
              value={tareForm.weight_kg}
              onChange={(e) =>
                setTareForm((prev) => ({ ...prev, weight_kg: e.target.value }))
              }
              autoFocus
            />
          </label>
          <label className={styles.field}>
            <span>Reason (optional)</span>
            <input
              type="text"
              maxLength={500}
              value={tareForm.reason}
              onChange={(e) =>
                setTareForm((prev) => ({ ...prev, reason: e.target.value }))
              }
              placeholder="e.g. annual re-tare"
            />
          </label>
          {tareError ? <p className={styles.error}>{tareError}</p> : null}
        </form>
      </Modal>
    </div>
  );
}
